import React, { memo } from 'react';
import ReactCountryFlag from 'react-country-flag';

interface CountryFlagProps {
  code: string;
  className?: string;
  style?: React.CSSProperties;
}

// Map language codes to country codes
const languageToCountryCode: Record<string, string> = {
  en: 'GB',
  'en-us': 'US',
  'en-gb': 'GB',
  'en-ca': 'JM', // English Caribbean (Jamaica)
  'en-jm': 'JM', // English Jamaica
  'en-scotland': 'GB', // English Scotland (usando GB, não há bandeira oficial da Escócia em emojis)
  'en-lanc': 'GB', // English Lancaster
  'en-lancaster': 'GB', // English Lancaster
  'en-gb-x-gbclan': 'GB', // English Lancaster (actual code)
  'en-rp': 'GB', // English Received Pronunciation
  'en-gb-x-rp': 'GB', // English Received Pronunciation (actual code)
  'en-wm': 'GB', // English West Midlands
  'en-gb-x-gbcwmd': 'GB', // English West Midlands (actual code)
  'en-gb-scotland': 'GB', // English Scotland (actual code)
  'en-029': 'JM', // English Caribbean (actual code)
  'en-uk': 'GB', // English UK (deprecated - use 'en-gb')
  'pt-br': 'BR',
  pt: 'PT',
  es: 'ES',
  'es-mx': 'MX',
  fr: 'FR',
  'fr-ca': 'CA',
  de: 'DE',
  it: 'IT',
  ja: 'JP',
  ko: 'KR',
  zh: 'CN',
  'zh-cn': 'CN',
  'zh-tw': 'TW',
  ru: 'RU',
  ar: 'SA',
  hi: 'IN',
  bn: 'BD',
  ur: 'PK',
  vi: 'VN',
  th: 'TH',
  tr: 'TR',
  nl: 'NL',
  sv: 'SE',
  no: 'NO',
  nb: 'NO', // Norwegian Bokmål
  da: 'DK',
  fi: 'FI',
  pl: 'PL',
  cs: 'CZ',
  sk: 'SK',
  hu: 'HU',
  ro: 'RO',
  bg: 'BG',
  hr: 'HR',
  sr: 'RS',
  sl: 'SI',
  et: 'EE',
  lv: 'LV',
  lt: 'LT',
  uk: 'UA',
  el: 'GR',
  he: 'IL',
  iw: 'IL', // Hebrew alternative code
  id: 'ID',
  ms: 'MY',
  tl: 'PH',
  sw: 'KE',
  am: 'ET',
  af: 'ZA',
  sq: 'AL',
  hy: 'AM',
  az: 'AZ',
  eu: 'ES', // Basque
  be: 'BY',
  bs: 'BA',
  an: 'ES', // Aragonese (Spain)
  ca: 'ES', // Catalan
  ceb: 'PH', // Cebuano
  co: 'FR', // Corsican
  cy: 'GB', // Welsh (Wales flag not available, using GB)
  eo: 'EU', // Esperanto (constructed language, using EU flag)
  'fa-latn': 'IR', // Persian (Pinglish) - Iran
  fa: 'IR',
  ga: 'IE',
  gd: 'GB', // Scottish Gaelic (Scotland flag not standard, using GB)
  gl: 'ES', // Galician
  gu: 'IN', // Gujarati
  ha: 'NG', // Hausa
  haw: 'US', // Hawaiian
  ht: 'HT',
  ig: 'NG', // Igbo
  is: 'IS',
  jv: 'ID', // Javanese
  ka: 'GE',
  kk: 'KZ',
  km: 'KH',
  kn: 'IN', // Kannada
  ku: 'IQ', // Kurdish
  ky: 'KG',
  la: 'VA', // Latin (Vatican)
  lb: 'LU',
  lo: 'LA',
  mg: 'MG',
  mi: 'NZ', // Maori
  mk: 'MK',
  ml: 'IN', // Malayalam
  mn: 'MN',
  mr: 'IN', // Marathi
  mt: 'MT',
  my: 'MM',
  ne: 'NP',
  ny: 'MW', // Nyanja/Chichewa
  or: 'IN', // Odia
  pa: 'IN', // Punjabi
  ps: 'AF', // Pashto
  qu: 'PE', // Quechua
  rw: 'RW', // Kinyarwanda
  sa: 'IN', // Sanskrit
  sd: 'PK', // Sindhi
  si: 'LK', // Sinhala
  sm: 'WS', // Samoan
  sn: 'ZW', // Shona
  so: 'SO',
  st: 'LS', // Sesotho
  su: 'ID', // Sundanese
  ta: 'IN', // Tamil
  te: 'IN', // Telugu
  tg: 'TJ',
  tk: 'TM',
  ts: 'ZA', // Tsonga
  tt: 'RU', // Tatar
  ug: 'CN', // Uyghur
  uz: 'UZ',
  xh: 'ZA', // Xhosa
  yi: 'IL', // Yiddish
  yo: 'NG', // Yoruba
  zu: 'ZA', // Zulu
  // Additional mappings for missing languages
  kl: 'GL', // Greenlandic (Greenland)
  grc: 'GR', // Ancient Greek (Greece)
  nci: 'MX', // Nahuatl (Mexico)
  nhe: 'MX', // Nahuatl Eastern (Mexico)
  nhw: 'MX', // Nahuatl Western (Mexico)
  quc: 'GT', // K'iche' (Guatemala)
  gn: 'PY', // Guarani (Paraguay)
  tup: 'BR', // Tupi (Brazil)
  tpw: 'BR', // Tupi (actual code)
  pap: 'AW', // Papiamento (Aruba)
  lfn: 'EU', // Lingua Franca Nova (constructed - EU flag)
  ia: 'EU', // Interlingua (constructed - EU flag)
  jbo: 'EU', // Lojban (constructed - EU flag)
  kok: 'IN', // Konkani (India)
  mni: 'IN', // Manipuri/Meitei (India)
  bpy: 'IN', // Bishnupriya Manipuri (India)
  as: 'IN', // Assamese (India)
  bho: 'IN', // Bhojpuri (India)
  dv: 'MV', // Dhivehi (Maldives)
  doi: 'IN', // Dogri (India)
  ilo: 'PH', // Ilocano (Philippines)
  ks: 'IN', // Kashmiri (India)
  mai: 'IN', // Maithili (India)
  'mni-mtei': 'IN', // Meiteilon/Manipuri (India)
  ay: 'BO', // Aymara (Bolivia)
  bm: 'ML', // Bambara (Mali)
  ee: 'GH', // Ewe (Ghana/Togo)
  kri: 'SL', // Krio (Sierra Leone)
  ln: 'CD', // Lingala (Congo)
  lg: 'UG', // Luganda (Uganda)
  ti: 'ER', // Tigrinya (Eritrea)
  fy: 'NL', // Frisian (Netherlands)
  hyw: 'AM', // Western Armenian
  ba: 'RU', // Bashkir (Russia)
  shan: 'MM', // Shan/Tai Yai (Myanmar)
  shn: 'MM', // Shan (actual code)
  yue: 'HK', // Cantonese (Hong Kong)
  hak: 'CN', // Hakka Chinese (China)
  cmn: 'CN', // Mandarin Chinese (China)
  'vi-central': 'VN', // Vietnamese Central
  'vi-north': 'VN', // Vietnamese Northern
  'vi-south': 'VN', // Vietnamese Southern
  'vi-vn-central': 'VN', // Vietnamese Central variant
  'vi-vn-north': 'VN', // Vietnamese Northern variant
  'vi-vn-south': 'VN', // Vietnamese Southern variant
  'vi-vn-x-central': 'VN', // Vietnamese Central (x- format)
  'vi-vn-x-south': 'VN', // Vietnamese Southern (x- format)
  om: 'ET', // Oromo (Ethiopia)
  tw: 'GH', // Twi (Ghana)
  tn: 'BW', // Tswana (Botswana)
};

// Fallback for unmapped codes
const getCountryCodeFromLanguage = (code: string): string => {
  const mapped = languageToCountryCode[code.toLowerCase()];
  if (mapped) return mapped;

  // Try to extract country code from language-COUNTRY format
  const parts = code.split('-');
  if (parts.length > 1) {
    const possibleCountry = parts[parts.length - 1].toUpperCase();
    // Check if it's a valid 2-letter country code
    if (possibleCountry.length === 2) {
      return possibleCountry;
    }
  }

  // Default fallback
  return 'UN';
};

export const CountryFlag = memo(function CountryFlag({
  code,
  className = '',
  style = {},
}: CountryFlagProps) {
  const countryCode = getCountryCodeFromLanguage(code);

  return (
    <ReactCountryFlag
      countryCode={countryCode}
      svg
      style={{
        width: '1.5em',
        height: '1.5em',
        ...style,
      }}
      className={className}
      title={code}
    />
  );
});

export default CountryFlag;
